export function parseContentDispositionFilename(header: string | undefined): string | null {
  if (!header) return null;

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1];

  const plainMatch = header.match(/filename=([^;]+)/i);
  if (plainMatch?.[1]) return plainMatch[1].trim();

  return null;
}

export function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function downloadBlobResponse(
  blob: Blob,
  contentDisposition: string | undefined,
  fallbackFilename: string,
) {
  const filename = parseContentDispositionFilename(contentDisposition) ?? fallbackFilename;
  triggerBrowserDownload(blob, filename);
}

function readResponseHeader(
  headers: Record<string, string | string[] | undefined>,
  name: string,
): string | undefined {
  const value = headers[name];
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
}

/** Prefer `x-download-filename`, then `Content-Disposition`; null when neither is present. */
export function getFilenameFromResponseHeaders(
  headers: Record<string, string | string[] | undefined>,
): string | null {
  const xDownload = readResponseHeader(headers, "x-download-filename")?.trim();
  if (xDownload) {
    return xDownload;
  }

  return parseContentDispositionFilename(readResponseHeader(headers, "content-disposition"));
}

export function decodeBase64ToBlob(content: string, mimeType: string): Blob {
  const binary = atob(content);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], {type: mimeType});
}

const TEMPLATE_MIME: Record<string, string> = {
  csv: "text/csv",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xls: "application/vnd.ms-excel",
};

export function getTemplateMimeType(fileType: string): string {
  return TEMPLATE_MIME[fileType] ?? "application/octet-stream";
}
